import { useEffect, useRef, useState } from 'react';
import { X, Printer, Download, ExternalLink, FileText } from 'lucide-react';
import type { BillPreview } from '@/hooks/api/useFolio';

interface BillPreviewModalProps {
  preview: BillPreview;
  onClose: () => void;
}

export function BillPreviewModal({ preview, onClose }: BillPreviewModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [pdfFailed, setPdfFailed] = useState(false);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Open in new tab (most reliable way to display PDFs)
  function handleOpenTab() {
    window.open(preview.blobUrl, '_blank');
  }

  // Print: open in new tab, browser's native print triggers automatically for PDFs
  function handlePrint() {
    const win = window.open(preview.blobUrl, '_blank');
    if (win) {
      // Give the PDF time to load then trigger print
      win.addEventListener('load', () => {
        try { win.print(); } catch { /* some browsers block this */ }
      });
    }
  }

  function handleDownload() {
    const a = document.createElement('a');
    a.href = preview.blobUrl;
    a.download = `Invoice-${preview.invoice.invoiceNumber}.pdf`;
    a.click();
  }

  const formattedDate = new Date(preview.invoice.issuedAt).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Invoice preview"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      {/* Blurred backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />

      {/* Modal panel */}
      <div className="relative z-10 flex flex-col w-full max-w-4xl h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0 bg-white">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold text-gray-900">
                Tax Invoice
              </h2>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-50 border border-primary-200 text-xs font-mono font-semibold text-primary-700 select-all">
                {preview.invoice.invoiceNumber}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {formattedDate}
              {preview.invoice.grossAmount && (
                <span className="ml-2 font-medium text-gray-700">
                  · Total: {parseFloat(preview.invoice.grossAmount).toFixed(2)}
                </span>
              )}
              <span className="ml-2 text-gray-400">· Copy Bill No. for tax claims</span>
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleOpenTab}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <ExternalLink size={14} aria-hidden="true" />
              Open
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <Printer size={14} aria-hidden="true" />
              Print
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <Download size={14} aria-hidden="true" />
              Download
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* PDF embed — object tag has better Chrome PDF support than iframe */}
        <div className="flex-1 bg-gray-50 overflow-hidden relative">
          {!pdfFailed ? (
            <object
              data={preview.blobUrl}
              type="application/pdf"
              className="w-full h-full border-0"
              aria-label="Invoice PDF"
              onError={() => setPdfFailed(true)}
            >
              {/* Fallback shown when object tag can't render */}
              <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center">
                  <FileText size={32} className="text-primary-600" aria-hidden="true" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-800 mb-1">PDF ready</p>
                  <p className="text-xs text-gray-500 mb-4">
                    Your browser couldn't display the PDF inline.
                  </p>
                  <button
                    type="button"
                    onClick={handleOpenTab}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
                  >
                    <ExternalLink size={14} aria-hidden="true" />
                    Open PDF in new tab
                  </button>
                </div>
              </div>
            </object>
          ) : (
            /* Explicit fallback if object tag fires onError */
            <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
              <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center">
                <FileText size={32} className="text-primary-600" aria-hidden="true" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-800 mb-1">Invoice generated</p>
                <p className="text-xs text-gray-500 mb-4">
                  Click below to view, print or download your invoice.
                </p>
                <button
                  type="button"
                  onClick={handleOpenTab}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  <ExternalLink size={14} aria-hidden="true" />
                  Open PDF in new tab
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
